/**
 * Typed wrappers for the access routes of the gateway (document/61 M1, M2,
 * M3, M4, M5, M10): tenants, the operator's panel, invitations, tenant roles,
 * API keys, the enforcement preview and the site's Pages project and domains.
 * Paths and bodies are exactly those of `apps/gateway/src/routes/{tenants,
 * operator,invitations,members,api-keys,site-delivery}.ts` and of
 * `packages/contracts/src/access.ts`; nothing here is invented.
 *
 * Kept apart from endpoints.ts so the screens that use them, loaded lazily,
 * carry these few functions in their own chunk.
 */
import type {
  AccessPreview,
  ApiKeySummary,
  AttachSitePagesDomainResult,
  CreateApiKeyRequest,
  CreatedApiKey,
  CreateInvitationRequest,
  CreateOperatorTenantRequest,
  CreateSitePagesProjectResult,
  CreateTenantBindingRequest,
  InvitationStatus,
  InvitationSummary,
  OperatorTenantSummary,
  SecurityDirectoryList,
  SetTenantMemberRoleRequest,
  SitePagesDomains,
  TenantBindingSummary,
  WriteResult,
} from '@cms/contracts'
import { apiFetch } from './client.js'
import type { WriteEnvelope } from './endpoints.js'

export interface ListResult<T> {
  items: T[]
  total: number
}

// ─── Operator (M1) ──────────────────────────────────────────────────────────

/** What `POST /operator/tenants` answers (apps/gateway/src/routes/operator.ts). */
export interface CreatedOperatorTenant {
  tenantId: string
  projectId: string
  siteId: string
  themeId: string
  menuId: string
  owner: { email: string; invitationId: string; expiresAt: number }
  binding: { issuer: string; externalTenantId: string } | null
  replayed: boolean
  nextSteps: string[]
}

export function listOperatorTenants(): Promise<ListResult<OperatorTenantSummary>> {
  return apiFetch('/operator/tenants')
}

/** The same Idempotency-Key on a retry lands on the same tenant: the wizard keeps one key per attempt. */
export function createOperatorTenant(body: CreateOperatorTenantRequest, idempotencyKey: string): Promise<CreatedOperatorTenant> {
  return apiFetch('/operator/tenants', { method: 'POST', body, idempotencyKey })
}

/** Binds an organization (expectedVersion 0) or restores a revoked binding (its current version). */
export function saveTenantBinding(body: CreateTenantBindingRequest, idempotencyKey: string): Promise<WriteResult> {
  return apiFetch('/operator/bindings', { method: 'POST', body, idempotencyKey })
}

export function revokeTenantBinding(binding: Pick<TenantBindingSummary, 'externalTenantId'>, body: WriteEnvelope, idempotencyKey: string): Promise<WriteResult & { sessionsClosed?: number }> {
  return apiFetch(`/operator/bindings/${encodeURIComponent(binding.externalTenantId)}`, { method: 'DELETE', body, idempotencyKey })
}

export function operatorAccessPreview(tenantId: string): Promise<AccessPreview> {
  return apiFetch(`/operator/tenants/${encodeURIComponent(tenantId)}/access-preview`)
}

/**
 * Whether the signed-in person is a platform operator, as `/auth/sso/me`
 * says. A gateway too old to say it is asked the cheapest operator-only
 * route instead, limited to one tenant: 403 means no. Only hides the menu
 * entry; every operator route checks again on the server.
 */
export async function probePlatformOperator(tenantId: string): Promise<boolean> {
  try {
    const me = await apiFetch<{ platformOperator?: boolean }>('/auth/sso/me')
    if (typeof me.platformOperator === 'boolean') return me.platformOperator
  } catch {
    // Fall through to the probe below.
  }
  try {
    await apiFetch('/operator/bindings', { query: { tenantId } })
    return true
  } catch {
    // 403 is the expected "no"; anything else (network, 5xx) is not a reason
    // to show a panel whose every call would fail the same way.
    return false
  }
}

// ─── People (M5) ────────────────────────────────────────────────────────────

/** The Security directory: the only place members and invitations are picked from. */
export function listSecurityDirectory(tenantId: string): Promise<SecurityDirectoryList> {
  return apiFetch(`/tenants/${encodeURIComponent(tenantId)}/directory`)
}

export function listInvitations(tenantId: string, status?: InvitationStatus): Promise<ListResult<InvitationSummary>> {
  return apiFetch(`/tenants/${encodeURIComponent(tenantId)}/invitations`, { query: { status } })
}

export function createInvitation(tenantId: string, body: CreateInvitationRequest, idempotencyKey: string): Promise<WriteResult & { email: string; role: string; expiresAt: number }> {
  return apiFetch(`/tenants/${encodeURIComponent(tenantId)}/invitations`, { method: 'POST', body, idempotencyKey })
}

export function revokeInvitation(tenantId: string, invitationId: string, body: WriteEnvelope, idempotencyKey: string): Promise<WriteResult> {
  return apiFetch(`/tenants/${encodeURIComponent(tenantId)}/invitations/${encodeURIComponent(invitationId)}`, { method: 'DELETE', body, idempotencyKey })
}

export function setTenantMemberRole(tenantId: string, personId: string, body: SetTenantMemberRoleRequest, idempotencyKey: string): Promise<WriteResult> {
  return apiFetch(`/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(personId)}/role`, { method: 'PUT', body, idempotencyKey })
}

export function tenantAccessPreview(tenantId: string): Promise<AccessPreview> {
  return apiFetch(`/tenants/${encodeURIComponent(tenantId)}/access-preview`)
}

// ─── API keys (M10) ─────────────────────────────────────────────────────────

export function listApiKeys(tenantId: string): Promise<ListResult<ApiKeySummary>> {
  return apiFetch(`/tenants/${encodeURIComponent(tenantId)}/api-keys`)
}

/** The only answer that carries the secret, once; a replay answers `key: null`. */
export function createApiKey(tenantId: string, body: CreateApiKeyRequest, idempotencyKey: string): Promise<CreatedApiKey> {
  return apiFetch(`/tenants/${encodeURIComponent(tenantId)}/api-keys`, { method: 'POST', body, idempotencyKey })
}

export function revokeApiKey(tenantId: string, keyId: string, body: WriteEnvelope, idempotencyKey: string): Promise<WriteResult> {
  return apiFetch(`/tenants/${encodeURIComponent(tenantId)}/api-keys/${encodeURIComponent(keyId)}`, { method: 'DELETE', body, idempotencyKey })
}

// ─── Site publishing (M2, M3) ───────────────────────────────────────────────

export function createSitePagesProject(siteId: string, body: WriteEnvelope, idempotencyKey: string): Promise<CreateSitePagesProjectResult> {
  return apiFetch(`/sites/${encodeURIComponent(siteId)}/delivery/project`, { method: 'POST', body, idempotencyKey })
}

export function getSitePagesDomains(siteId: string): Promise<SitePagesDomains> {
  return apiFetch(`/sites/${encodeURIComponent(siteId)}/delivery/domains`)
}

export function attachSitePagesDomain(siteId: string, body: WriteEnvelope & { domain: string }, idempotencyKey: string): Promise<AttachSitePagesDomainResult> {
  return apiFetch(`/sites/${encodeURIComponent(siteId)}/delivery/domains`, { method: 'POST', body, idempotencyKey })
}
